import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FlowState } from "../../pages/FlowPage";
import { BigButton } from "../BigButton";

interface Props {
  state: FlowState;
  onNext: (update: Partial<FlowState>) => void;
}

// USB HID barcode scanners fire keystrokes much faster than a human can type.
// If consecutive keydown events arrive within this threshold we treat the input
// as a scan and auto-advance; otherwise it is manual typing.
const HID_MAX_GAP_MS = 50;
const HID_MIN_LENGTH = 3;

type Mode = "scan" | "manual";

export function RefStep({ state, onNext }: Props) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("scan");
  const [value, setValue] = useState(state.external_reference ?? "");

  // HID scan buffer
  const bufRef = useRef("");
  const lastKeyTimeRef = useRef(0);

  const handleManualContinue = useCallback(() => {
    onNext({ external_reference: value.trim() || undefined });
  }, [onNext, value]);

  // Global keydown listener for HID scanner in "scan" mode.
  useEffect(() => {
    if (mode !== "scan") return;

    function onKeyDown(e: KeyboardEvent) {
      const now = Date.now();
      const gap = now - lastKeyTimeRef.current;
      lastKeyTimeRef.current = now;

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const scanned = bufRef.current.trim();
        bufRef.current = "";
        if (scanned.length >= HID_MIN_LENGTH) {
          onNext({ external_reference: scanned || undefined });
        }
        return;
      }

      // Non-printable keys (Escape, Backspace, etc.) clear the buffer.
      if (e.key.length !== 1) {
        bufRef.current = "";
        return;
      }

      if (gap > HID_MAX_GAP_MS && bufRef.current.length > 0) {
        // Gap too large — user is typing manually; switch to manual mode.
        const partial = bufRef.current + e.key;
        bufRef.current = "";
        setValue(partial);
        setMode("manual");
        return;
      }

      bufRef.current += e.key;
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mode, onNext]);

  return (
    <div className="flex flex-col gap-6 p-8 h-full justify-center">
      <h2 className="text-3xl font-bold text-center">{t("flow.ref.title")}</h2>

      {mode === "scan" ? (
        <>
          <p className="text-center text-[var(--kiosk-brand-muted)] text-lg">
            {t("flow.ref.scanSubtitle")}
          </p>

          {/* Visual indicator */}
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="w-24 h-24 rounded-2xl border-4 border-[var(--kiosk-brand-accent)] flex items-center justify-center">
              <svg
                className="w-12 h-12 text-[var(--kiosk-brand-accent)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75V16.5zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z"
                />
              </svg>
            </div>
            <p className="text-lg text-[var(--kiosk-brand-muted)] animate-pulse">
              {t("flow.ref.waiting")}
            </p>
          </div>

          <BigButton
            variant="secondary"
            fullWidth
            onClick={() => setMode("manual")}
          >
            {t("flow.ref.typeManually")}
          </BigButton>
        </>
      ) : (
        <>
          <p className="text-center text-[var(--kiosk-brand-muted)] text-lg">
            {t("flow.ref.manualSubtitle")}
          </p>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleManualContinue()}
            placeholder={t("flow.ref.placeholder")}
            className="w-full rounded-[var(--kiosk-brand-radius)] border-2 border-[var(--kiosk-brand-primary)] px-6 py-4 text-2xl focus:outline-none focus:border-[var(--kiosk-brand-accent)] bg-[var(--kiosk-brand-surface)]"
            aria-label={t("flow.ref.title")}
            autoFocus
          />
          <BigButton variant="primary" fullWidth onClick={handleManualContinue}>
            {t("flow.next")}
          </BigButton>
        </>
      )}

      <BigButton
        variant="secondary"
        fullWidth
        onClick={() => onNext({ external_reference: undefined })}
      >
        {t("flow.ref.skip")}
      </BigButton>
    </div>
  );
}
